import { useState, FormEvent } from "react";

interface Props {
  lang: "en" | "ru";
}

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function K4MQuoteForm({ lang }: Props) {
  const isRu = lang === "ru";
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setStatus("submitting");
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d7cac9uhe76khnho51sg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="k4m-quote" className="py-24 px-6" style={{ background: "#F4F5F7" }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left: Info */}
          <div>
            <p className="text-xs tracking-widest uppercase mb-4 font-medium" style={{ color: "#D97706", letterSpacing: "0.25em" }}>
              {isRu ? "Получить оптовый прайс-лист" : "Request B2B Quote"}
            </p>
            <h2 className="text-3xl md:text-4xl font-thin mb-4" style={{ letterSpacing: "0.04em", color: "#1a2332" }}>
              {isRu ? "ЗАПРОСИТЬ" : "GET YOUR"}
              <br />
              <span style={{ color: "#D97706" }}>
                {isRu ? "ОПТОВЫЙ ПРАЙС." : "B2B QUOTE."}
              </span>
            </h2>
            <div className="mb-8" style={{ width: "60px", height: "2px", background: "#F59E0B" }} />

            <p className="text-sm font-light leading-relaxed mb-10" style={{ color: "#5a6a7e", lineHeight: "1.85" }}>
              {isRu
                ? "Заполните форму — наша команда ответит в течение 24 часов с актуальным прайс-листом, условиями поставки и информацией о наличии двигателей K4M."
                : "Submit your requirements and our B2B team will respond within 24 hours with pricing, availability, and MOQ data for Renault K4M engine wholesale."}
            </p>

            {/* Contact info */}
            <div className="flex flex-col gap-5">
              <a
                href="https://wa.me/8613522126700"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                  style={{ background: "rgba(37,211,102,0.1)", border: "1px solid rgba(37,211,102,0.3)" }}
                >
                  <i className="ri-whatsapp-line text-lg" style={{ color: "#25D366" }} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(90,106,126,0.5)", letterSpacing: "0.15em" }}>
                    WhatsApp
                  </p>
                  <p className="text-sm font-medium transition-colors duration-200 group-hover:text-green-600" style={{ color: "#1a2332" }}>
                    +86 13522126700 (Text Only)
                  </p>
                </div>
              </a>
              <a href="mailto:chirunnexa@gmail.com" className="flex items-center gap-4 group cursor-pointer">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                  style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}
                >
                  <i className="ri-mail-line text-lg" style={{ color: "#D97706" }} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(90,106,126,0.5)", letterSpacing: "0.15em" }}>
                    Email
                  </p>
                  <p className="text-sm font-medium transition-colors duration-200 group-hover:text-amber-600" style={{ color: "#1a2332" }}>
                    chirunnexa@gmail.com
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="rounded-2xl p-8 md:p-10 bg-white"
            style={{ border: "1px solid rgba(0,0,0,0.07)" }}
          >
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-full mb-6"
                  style={{ background: "rgba(245,158,11,0.1)", border: "2px solid rgba(245,158,11,0.4)" }}
                >
                  <i className="ri-check-line text-2xl" style={{ color: "#D97706" }} />
                </div>
                <h3 className="text-xl font-light mb-3" style={{ color: "#1a2332" }}>
                  {isRu ? "Запрос отправлен!" : "Inquiry Submitted!"}
                </h3>
                <p className="text-sm" style={{ color: "#5a6a7e" }}>
                  {isRu ? "Наша команда ответит в течение 24 часов." : "Our team will respond within 24 business hours."}
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 px-6 py-2 rounded-lg text-sm font-medium transition-all hover:brightness-110 whitespace-nowrap cursor-pointer"
                  style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", color: "#D97706" }}
                >
                  {isRu ? "Отправить ещё" : "Submit Another"}
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="k4m-b2b-quote-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div>
                  <label className="block text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: "rgba(90,106,126,0.65)", letterSpacing: "0.15em" }}>
                    {isRu ? "Имя *" : "Full Name *"}
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    placeholder={isRu ? "Иван Иванов" : "John Smith"}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                    style={{ background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1a2332", fontSize: "0.875rem" }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.55)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")}
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: "rgba(90,106,126,0.65)", letterSpacing: "0.15em" }}>
                    {isRu ? "Название компании *" : "Company Name *"}
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    required
                    placeholder={isRu ? "ООО Ваша Компания" : "Your Company Ltd."}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                    style={{ background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1a2332", fontSize: "0.875rem" }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.55)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")}
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: "rgba(90,106,126,0.65)", letterSpacing: "0.15em" }}>
                    {isRu ? "Email *" : "Email Address *"}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={isRu ? "you@company.ru" : "you@company.com"}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                    style={{ background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1a2332", fontSize: "0.875rem" }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.55)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")}
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: "rgba(90,106,126,0.65)", letterSpacing: "0.15em" }}>
                    {isRu ? "Объём заказа *" : "Order Quantity *"}
                  </label>
                  <select
                    name="orderQuantity"
                    required
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 cursor-pointer"
                    style={{ background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1a2332", fontSize: "0.875rem" }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.55)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")}
                  >
                    <option value="">{isRu ? "Выберите объём" : "Select quantity"}</option>
                    <option value="1-5">{isRu ? "1–5 единиц (пробная партия)" : "1–5 units (trial order)"}</option>
                    <option value="6-20">{isRu ? "6–20 единиц" : "6–20 units"}</option>
                    <option value="21-50">{isRu ? "21–50 единиц" : "21–50 units"}</option>
                    <option value="50+">{isRu ? "50+ единиц (FCL)" : "50+ units (FCL)"}</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs uppercase tracking-widest font-medium" style={{ color: "rgba(90,106,126,0.65)", letterSpacing: "0.15em" }}>
                      {isRu ? "Дополнительные требования" : "Additional Requirements"}
                    </label>
                    <span className="text-xs" style={{ color: charCount > 480 ? "#ef4444" : "rgba(90,106,126,0.4)" }}>
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="requirements"
                    rows={4}
                    maxLength={500}
                    placeholder={isRu
                      ? "Укажите применяемость, год выпуска, особые требования к комплектации..."
                      : "Vehicle application, model year, specific configuration requirements, destination port..."}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 resize-none"
                    style={{ background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1a2332", fontSize: "0.875rem" }}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.55)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")}
                  />
                </div>

                {status === "error" && (
                  <p className="text-xs text-red-500">
                    {isRu ? "Ошибка отправки. Попробуйте ещё раз или напишите нам напрямую." : "Submission failed. Please try again or contact us directly."}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting" || charCount > 500}
                  className="w-full py-4 rounded-xl font-bold text-xs tracking-widest transition-all duration-200 hover:brightness-110 active:scale-95 disabled:opacity-50 whitespace-nowrap cursor-pointer text-white"
                  style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.15em" }}
                >
                  {status === "submitting"
                    ? (isRu ? "ОТПРАВКА..." : "SUBMITTING...")
                    : (isRu ? "ПОЛУЧИТЬ ОПТОВЫЙ ПРАЙС-ЛИСТ" : "REQUEST B2B QUOTE")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
